import React from 'react';
import { motion } from 'framer-motion';

const caseStudies = [
  {
    title: 'Multi-Tenant SaaS Platform',
    description:
      'Designed a scalable multi-tenant SaaS architecture using Laravel with tenant isolation, Redis queues, and modular service layers. The system supported thousands of users with secure tenant-level data separation.',
    keyPoints: [
      'Tenant-aware database architecture',
      'Queue-driven background jobs',
      'Modular Laravel service architecture',
      'Redis caching for performance',
    ],
    technologies: ['Laravel', 'MySQL', 'Redis', 'AWS S3'],
  },
  {
    title: 'Construction Workforce Platform',
    description:
      'Developed a real-time workforce management system used for tracking construction worker hours, GPS entries, and project allocations across multiple companies.',
    keyPoints: [
      'GPS worker tracking',
      'Real-time reporting dashboards',
      'Multi-company architecture',
      'Optimized MySQL queries',
    ],
    technologies: ['Laravel', 'Vue.js', 'MySQL', 'Redis'],
  },
  {
    title: 'Podcast Hosting SaaS',
    description:
      'Built a scalable SaaS podcast hosting platform supporting media uploads, audio processing jobs, and distributed storage.',
    keyPoints: [
      'Background audio processing jobs',
      'Media storage with AWS S3',
      'Subscription billing system',
      'Queue-based processing',
    ],
    technologies: ['Laravel', 'Redis Queues', 'AWS S3'],
  },
];

const ArchitectureCaseStudiesV2 = () => {
  return (
    <section className="py-16 md:py-20 lg:py-24 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="w-full rounded-2xl bg-white border border-gray-200 p-6 md:p-8">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 tracking-tight text-gray-900">
          Architecture Case Studies
        </h2>
        <div className="w-16 h-1 bg-blue-600 rounded mt-2 mb-6"></div>
        <p className="text-sm md:text-base text-gray-600 max-w-2xl mb-8">
          Real system architecture decisions applied in production platforms with scalability, performance, and maintainability in focus.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {caseStudies.map((study, index) => (
            <motion.article
              key={study.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: index * 0.08 }}
              viewport={{ once: true }}
              whileHover={{ y: -8 }}
              className="bg-gray-50 border border-gray-200 rounded-xl p-6 md:p-8 shadow-md hover:shadow-lg hover:-translate-y-1 transition-all duration-300 space-y-3"
            >
              <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-3">{study.title}</h3>
              <p className="max-w-prose text-sm md:text-base text-gray-600 leading-relaxed mb-5">{study.description}</p>

              <div className="mb-5">
                <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3">Key Points</h4>
                <ul className="space-y-2">
                  {study.keyPoints.map((point) => (
                    <li key={point} className="text-sm md:text-base text-gray-600 leading-relaxed">
                      • {point}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3">Technologies</h4>
                <div className="flex flex-wrap gap-2">
                  {study.technologies.map((tech) => (
                    <span
                      key={tech}
                      className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
      </div>
    </section>
  );
};

export default ArchitectureCaseStudiesV2;
